import { Inject, Injectable, signal } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { BehaviorSubject, Observable } from "rxjs";
import { tap } from "rxjs/operators";
import { Router } from "@angular/router";
import { LoginRequest } from "@shared/schemas/login-request";
import { LocalStorageService } from "@core/services/storage/local-storage.service";
import {
	EnvironmentConfig,
	ENV_CONFIG
} from "@core/environments/environment-config";
import { skipAuth } from "@core/interceptors/token.interceptor";
import { NgxRolesService } from "ngx-permissions";
import { isUserAccountant, isUserSupport } from "@shared/utils/user";
import { UserStorage } from "@shared/schemas/enums/user-storage";
import { BusinessService } from "@data/services/business.service";
import { CookieService } from "ngx-cookie-service";
import {
	CompleteResponse,
	LoginResponse
} from "@shared/schemas/complete-login";
import { ignoreHttpError } from "@core/interceptors/http-error.interceptor";
import { environment } from "@env";
import { ZendeskService } from "../zendesk.service";

@Injectable({
	providedIn: "root"
})
export class AuthService {
	API_URL = "";
	API_FORCE_LOGIN_URL = "";
	API_FORCE_ASAAS_KEY_URL = "";
	URL_LOGIN_ASAAS = "";

	username = "";

	isPrereleaseZendeskUser = signal(false);

	private isRouterEventDone = false;

	private userLoggedIn = new BehaviorSubject<LoginResponse | undefined>(
		undefined
	);
	userLoggedIn$ = this.userLoggedIn.asObservable();

	constructor(
		private http: HttpClient,
		private localStorage: LocalStorageService,
		private router: Router,
		private roles: NgxRolesService,
		private businessService: BusinessService,
		private cookieService: CookieService,
		private zendeskService: ZendeskService,
		@Inject(ENV_CONFIG) private config: EnvironmentConfig
	) {
		this.API_URL = `${config.environment.API_OAUTH}`;
		this.API_FORCE_LOGIN_URL = `${config.environment.API}/support-users/force-login-by-support-user`;
		this.API_FORCE_ASAAS_KEY_URL = `${config.environment.API}/management/users/token`;
		this.URL_LOGIN_ASAAS = environment.production
			? "https://www.asaas.com"
			: "https://sandbox.asaas.com";
	}

	setSupportLoggedInAsNormalUser(): void {
		this.localStorage.set("supportLoggedInAsNormalUser", true);
	}

	removeSupportLoggedInAsNormalUser(): void {
		this.localStorage.remove("supportLoggedInAsNormalUser");
	}

	removeLoginByAssas(): void {
		this.localStorage.remove("loginFromAssas");
		this.localStorage.remove("apiKeyFromAsaas");
	}

	isSupportLoggedInAsNormalUser(): boolean {
		return !!this.localStorage.get("supportLoggedInAsNormalUser");
	}

	private setSessionSupportAccountant(authResult: LoginResponse): void {
		if (
			isUserSupport(authResult.tipoUsuario) ||
			isUserAccountant(authResult.tipoUsuario)
		) {
			this.localStorage.set<LoginResponse>(
				UserStorage.UserLoginSupportAccountant,
				authResult
			);
		}
	}

	setSession(authResult: LoginResponse) {
		this.setSessionSupportAccountant(authResult);
		this.localStorage.set<LoginResponse>(UserStorage.UserLogin, authResult);
		if (authResult.hashedFingerprint) {
			this.localStorage.set("fingerprint", authResult.hashedFingerprint);
		}
		this.userLoggedIn.next(authResult);
	}

	login(loginRequest: LoginRequest): Observable<CompleteResponse> {
		return this.http
			.post<CompleteResponse>(
				`${this.config.environment.API_BFF}/auth/login`,
				loginRequest,
				{ withCredentials: true, context: ignoreHttpError() }
			)
			.pipe(
				tap(({ loginResponse }) => {
					this.localStorage.set(
						"jwtZendesk",
						loginResponse.jwtZendesk
					);
				})
			);
	}

	forceLoginSupportUser(
		supportClientId: string,
		supportUserId: string
	): Observable<CompleteResponse> {
		return this.http.post<CompleteResponse>(
			`${this.config.environment.API_BFF}/auth/login-as-user`,
			{
				supportClientId,
				supportUserId
			}
		);
	}

	logoutSupport(redirect = true): Observable<unknown> {
		return this.http
			.post(
				`${this.config.environment.API_BFF}/auth/logout-support`,
				{
					base: "true"
				},
				{ withCredentials: true }
			)

			.pipe(
				tap(() => {
					if (!this.isSupportLoggedInAsNormalUser()) {
						this.localStorage.remove("fingerprint");
					}
					this.localStorage.remove(UserStorage.UserLogin);
					this.deleteAllCookies();
					this.roles.flushRolesAndPermissions();
					this.isRouterEventDone = false;
					this.businessService.clearBusinessData();
					this.zendeskService.hideZendesk();
					if (redirect) {
						this.mapRouteToUserType();
					}
				})
			);
	}

	loginLegacy(): Observable<unknown> {
		const urlBaseErp = this.config.environment.PROXY_BASEERP;

		const params = new HttpParams()
			.set("base", true)
			.set("validacaoLegado", false)
			.set("token", this.getToken());

		return this.http.get(`${urlBaseErp}/autenticacao/`, {
			params
		});
	}

	logoutLegacy(): Observable<unknown> {
		const urlBaseErp = this.config.environment.PROXY_BASEERP;
		return this.http.delete(
			`${urlBaseErp}/autenticacao/?base=false&token=${this.getToken()}`
		);
	}

	logout(redirect = true): Observable<unknown> {
		return this.http
			.post(
				`${this.config.environment.API_BFF}/auth/logout`,
				{
					base: "true"
				},
				{ withCredentials: true }
			)
			.pipe(
				tap(() => {
					this.localStorage.remove(UserStorage.UserLogin);
					this.localStorage.remove("fingerprint");
					this.localStorage.remove("jwtZendesk");
					this.deleteAllCookies();
					this.roles.flushRolesAndPermissions();
					this.isRouterEventDone = false;
					this.businessService.clearBusinessData();
					this.zendeskService.hideZendesk();
					if (redirect) this.mapRouteToUserType();
				})
			);
	}

	loginByAsaas(key: string): Observable<CompleteResponse> {
		return this.http
			.get<CompleteResponse>(
				`${this.config.environment.API_BFF}/auth/login-by-asaas?api-key=${key}`,
				{ withCredentials: true, context: ignoreHttpError() }
			)
			.pipe(
				tap((response) => {
					this.localStorage.set("loginFromAssas", true);
					this.localStorage.set(
						"jwtZendesk",
						response.loginResponse.jwtZendesk
					);
					this.setSession(response.loginResponse);
				})
			);
	}

	mapRouteToUserType(): void {
		if (this.isSupportLoggedInAsNormalUser()) {
			this.removeSupportLoggedInAsNormalUser();
			void this.router.navigateByUrl("/suporte/v2");
			return;
		}

		this.removeLoginByAssas();
		window.location.href = this.getCompleteAsaasLoginUrl();
	}

	clearSession(): void {
		this.localStorage.clear();
	}

	logoutUserSupportAccountant(): void {
		this.localStorage.remove(UserStorage.UserLoginSupportAccountant);
		this.setRouterEventNotDone();
	}

	clearAllUserData(): void {
		this.localStorage.remove(UserStorage.UserLogin);
		this.localStorage.remove(UserStorage.UserLoginSupportAccountant);
	}

	hasPermissionsLoaded(): boolean {
		return Object.keys(this.roles.getRoles()).length > 0;
	}

	isLoggedIn(): boolean {
		return !!this.getToken();
	}

	getUserLogin(): LoginResponse | null {
		return this.localStorage.get<LoginResponse>(UserStorage.UserLogin);
	}

	setRouterEventDone(): void {
		this.isRouterEventDone = true;
	}

	setRouterEventNotDone(): void {
		this.isRouterEventDone = false;
	}

	getRouterEventDone(): boolean {
		return this.isRouterEventDone;
	}

	canGoToHome(): boolean {
		if (this.getUserSupport()) {
			return (
				this.isLoggedIn() &&
				this.hasPermissionsLoaded() &&
				this.isSupportLoggedInAsNormalUser() &&
				this.isRouterEventDone
			);
		}
		return (
			this.isLoggedIn() &&
			this.hasPermissionsLoaded() &&
			this.isRouterEventDone
		);
	}

	isLoggedOut(): boolean {
		return !this.isLoggedIn();
	}

	getExpiration(): number {
		const expirationTimer = this.localStorage.get<LoginResponse>(
			UserStorage.UserLogin
		)?.expires_in;
		return expirationTimer ?? 0;
	}

	getExpirationUnix(): number {
		const expirationTimerUnix = this.localStorage.get<LoginResponse>(
			UserStorage.UserLogin
		)?.expUnix;
		return expirationTimerUnix ?? 0;
	}

	getToken(): string {
		return (
			this.localStorage.get<LoginResponse>(UserStorage.UserLogin)
				?.access_token ?? ""
		);
	}

	removeUserToken(): void {
		this.localStorage.remove(UserStorage.UserLogin);
	}

	getTokenSupportAccountant(): string {
		return (
			this.localStorage.get<LoginResponse>(
				UserStorage.UserLoginSupportAccountant
			)?.access_token ?? ""
		);
	}

	getRefreshToken(): string | undefined {
		return this.localStorage.get<LoginResponse>(UserStorage.UserLogin)
			?.refresh_token;
	}

	getUserType(): string {
		return (
			this.localStorage.get<LoginResponse>(UserStorage.UserLogin)
				?.tipoUsuario ?? ""
		);
	}

	getUserSupportAccountantType(): string {
		return (
			this.localStorage.get<LoginResponse>(
				UserStorage.UserLoginSupportAccountant
			)?.tipoUsuario ?? ""
		);
	}

	getUserSupport(): LoginResponse | null {
		return this.localStorage.get<LoginResponse>(
			UserStorage.UserLoginSupportAccountant
		);
	}

	refreshToken(): Observable<LoginResponse> {
		const options = {
			context: skipAuth(),
			withCredentials: true
		};

		return this.http
			.post<LoginResponse>(
				`${this.config.environment.API_BFF}/auth/refresh-token`,
				{
					username: this.username,
					refresh_token: this.getRefreshToken() ?? "",
					hashedFingerprint: this.getHashedFingerprint() ?? ""
				},
				options
			)
			.pipe(tap((response) => this.setSession(response)));
	}

	isLogoutBySupportAccountant(): boolean {
		return !!this.localStorage.get<LoginResponse>(UserStorage.UserLogin);
	}

	isBaseAdm(): boolean {
		return this.localStorage.get<LoginResponse>(UserStorage.UserLogin)
			?.isBaseAdm as boolean;
	}

	deleteAllCookies(): void {
		this.cookieService.deleteAll("/");
	}

	isLoginFromAsaas(): boolean {
		return !!this.localStorage.get("loginFromAssas");
	}

	getHashedFingerprint(): string | undefined {
		return this.localStorage.get("fingerprint") ?? "";
	}

	getUserLoginData(): LoginResponse | null {
		return this.getUserSupport()
			? this.getUserSupport()
			: this.getUserLogin();
	}

	getAuthorityAsaas(): string | undefined {
		return this.getUserLogin()?.authorityAsaas;
	}

	getCompleteAsaasLoginUrl(): string {
		return `${this.URL_LOGIN_ASAAS}/j_spring_security_logout?logoutRedirectReason=ASAAS_ERP_LOGOUT`;
	}
}
